const DEFAULTS = Object.freeze({
  gridW: 40,
  gridH: 40,
  minLeaf: 10,
  minRoom: 7,
  pad: 2,
  depth: 3,
  corridorWidth: 3,
});

function mkRng(seed) {
  let s = seed | 0;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function bsp(x, y, w, h, depth, rng, cfg) {
  const node = { x, y, w, h, left: null, right: null, room: null };
  if (depth === 0 || w < cfg.minLeaf * 2 || h < cfg.minLeaf * 2) {
    const maxW = Math.max(cfg.minRoom, w - cfg.pad * 2);
    const maxH = Math.max(cfg.minRoom, h - cfg.pad * 2);
    const rw = cfg.minRoom + Math.floor(rng() * (maxW - cfg.minRoom + 1));
    const rh = cfg.minRoom + Math.floor(rng() * (maxH - cfg.minRoom + 1));
    const rx = x + cfg.pad + Math.floor(rng() * Math.max(1, w - rw - cfg.pad * 2));
    const ry = y + cfg.pad + Math.floor(rng() * Math.max(1, h - rh - cfg.pad * 2));
    node.room = {
      x: rx,
      y: ry,
      w: rw,
      h: rh,
      cx: Math.floor(rx + rw / 2),
      cy: Math.floor(ry + rh / 2),
      area: rw * rh,
    };
    return node;
  }
  const splitHoriz = (w < h) || (!(h < w) && rng() < 0.5);
  if (splitHoriz) {
    const split = cfg.minLeaf + Math.floor(rng() * Math.max(1, h - cfg.minLeaf * 2));
    node.left = bsp(x, y, w, split, depth - 1, rng, cfg);
    node.right = bsp(x, y + split, w, h - split, depth - 1, rng, cfg);
  } else {
    const split = cfg.minLeaf + Math.floor(rng() * Math.max(1, w - cfg.minLeaf * 2));
    node.left = bsp(x, y, split, h, depth - 1, rng, cfg);
    node.right = bsp(x + split, y, w - split, h, depth - 1, rng, cfg);
  }
  return node;
}

function collectRooms(node, out) {
  if (node.room) {
    out.push(node.room);
    return;
  }
  if (node.left) collectRooms(node.left, out);
  if (node.right) collectRooms(node.right, out);
}

function getRoom(node, rng) {
  if (node.room) return node.room;
  const l = node.left ? getRoom(node.left, rng) : null;
  const r = node.right ? getRoom(node.right, rng) : null;
  if (!l) return r;
  if (!r) return l;
  return rng() < 0.5 ? l : r;
}

function paintCorridor(grid, x1, y1, x2, y2, cw, cfg) {
  const half = Math.floor(cw / 2);
  const midX = x1;
  const midY = y2;
  const stampH = (gx, gy) => {
    for (let o = -half; o < cw - half; o++) {
      const cx = gx + o;
      if (cx >= 0 && cx < cfg.gridW && gy >= 0 && gy < cfg.gridH) grid[gy * cfg.gridW + cx] = 1;
    }
  };
  const stampV = (gy, gx) => {
    for (let o = -half; o < cw - half; o++) {
      const cy = gy + o;
      if (cy >= 0 && cy < cfg.gridH && gx >= 0 && gx < cfg.gridW) grid[cy * cfg.gridW + gx] = 1;
    }
  };
  for (let x = Math.min(x1, midX); x <= Math.max(x1, midX); x++) stampH(x, y1);
  for (let y = Math.min(y1, midY); y <= Math.max(y1, midY); y++) stampV(y, midX);
  for (let x = Math.min(midX, x2); x <= Math.max(midX, x2); x++) stampH(x, y2);
}

function connectTree(node, grid, rng, cfg) {
  if (!node.left || !node.right) return;
  const a = getRoom(node.left, rng);
  const b = getRoom(node.right, rng);
  if (a && b) paintCorridor(grid, a.cx, a.cy, b.cx, b.cy, cfg.corridorWidth, cfg);
  connectTree(node.left, grid, rng, cfg);
  connectTree(node.right, grid, rng, cfg);
}

function roomDist(a, b) {
  return Math.abs(a.cx - b.cx) + Math.abs(a.cy - b.cy);
}

function assignRoles(rooms) {
  const sorted = [...rooms].sort((a, b) => a.area - b.area);
  const doorA = sorted[0];
  let doorB = rooms[0];
  let maxD = 0;
  for (const r of rooms) {
    if (r === doorA) continue;
    const d = roomDist(doorA, r);
    if (d > maxD) {
      maxD = d;
      doorB = r;
    }
  }
  const bossPool = rooms
    .filter((r) => r !== doorA && r !== doorB && r.area >= 81)
    .sort((a, b) => b.area - a.area);
  const boss = bossPool.length ? bossPool[0] : sorted[sorted.length - 1];
  const enemyRooms = rooms.filter((r) => r !== doorA && r !== doorB && r !== boss);

  const spawns = [
    { type: 'doorA', room: doorA, cellX: doorA.cx, cellY: doorA.cy },
    { type: 'doorB', room: doorB, cellX: doorB.cx, cellY: doorB.cy },
    { type: 'boss', room: boss, cellX: boss.cx, cellY: boss.cy },
  ];

  for (const r of enemyRooms) {
    const count = r.area >= 64 ? 3 : r.area >= 49 ? 2 : 1;
    const ox = [0, -2, 2, 0, -2, 2].slice(0, count);
    const oy = [-1, 0, 0, 2, 2, -2].slice(0, count);
    for (let i = 0; i < count; i++) {
      spawns.push({ type: 'enemy', room: r, cellX: r.cx + ox[i], cellY: r.cy + oy[i] });
    }
  }
  return { doorA, doorB, boss, enemyRooms, spawns, maxD };
}

function clampSpawnToFloor(grid, cellX, cellY, cfg) {
  const inBounds = (x, y) => x >= 0 && x < cfg.gridW && y >= 0 && y < cfg.gridH;
  if (inBounds(cellX, cellY) && grid[cellY * cfg.gridW + cellX] === 1) return { cellX, cellY };
  for (let r = 1; r <= 3; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const nx = cellX + dx;
        const ny = cellY + dy;
        if (inBounds(nx, ny) && grid[ny * cfg.gridW + nx] === 1) return { cellX: nx, cellY: ny };
      }
    }
  }
  return null;
}

export function generateSpawnMarkerLevel(seed, options = {}) {
  const cfg = { ...DEFAULTS, ...options };
  const rng = mkRng(seed);
  const grid = new Uint8Array(cfg.gridW * cfg.gridH);
  const root = bsp(0, 0, cfg.gridW, cfg.gridH, cfg.depth, rng, cfg);
  const rooms = [];
  collectRooms(root, rooms);
  for (const r of rooms) {
    for (let dy = 0; dy < r.h; dy++) {
      for (let dx = 0; dx < r.w; dx++) {
        grid[(r.y + dy) * cfg.gridW + (r.x + dx)] = 1;
      }
    }
  }
  connectTree(root, grid, rng, cfg);

  for (let cy = 0; cy < cfg.gridH; cy++) {
    for (let cx = 0; cx < cfg.gridW; cx++) {
      if (grid[cy * cfg.gridW + cx] !== 0) continue;
      let adjacentFloor = false;
      for (let dy = -1; dy <= 1 && !adjacentFloor; dy++) {
        for (let dx = -1; dx <= 1 && !adjacentFloor; dx++) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx >= 0 && nx < cfg.gridW && ny >= 0 && ny < cfg.gridH && grid[ny * cfg.gridW + nx] === 1) {
            adjacentFloor = true;
          }
        }
      }
      if (adjacentFloor) grid[cy * cfg.gridW + cx] = 2;
    }
  }

  const roles = assignRoles(rooms);
  const spawns = [];
  for (const spawn of roles.spawns) {
    const clamped = clampSpawnToFloor(grid, spawn.cellX, spawn.cellY, cfg);
    if (!clamped) continue;
    spawns.push({ ...spawn, cellX: clamped.cellX, cellY: clamped.cellY });
  }

  return {
    grid,
    rooms,
    spawns,
    doorA: { ...roles.doorA },
    doorB: { ...roles.doorB },
    boss: { ...roles.boss },
    enemyRooms: [...roles.enemyRooms],
    maxD: roles.maxD,
    config: cfg,
  };
}
